import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type SubscriptionUser from 'lumen/models/subscription-user';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

export interface SubscriptionUserMeta {
  count: number;
  page: number;
  page_count: number;
  page_size: number;
}

export interface SubscriptionUserData {
  meta: SubscriptionUserMeta;
  users: SubscriptionUser[];
}

interface RouteParams {
  page?: number;
  pageSize?: number;
}

export interface UsersModel {
  loadUsersTask: typeof HotelSettingsUsersRoute.prototype.loadUsersTask;
}

interface RouteParams {
  sortBy?: string;
  sortDirection?: string;
}

export default class HotelSettingsUsersRoute extends Route {
  queryParams = {
    sortBy: { refreshModel: true },
    sortDirection: { refreshModel: true },
    page: { refreshModel: true },
    pageSize: { refreshModel: true },
  };

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  loadUsersTask = task({ restartable: true }, async (params: RouteParams) => {
    try {
      const subscriptionId = this.hotelAndMarketService.hotel.id;
      const response = await this.store.query<SubscriptionUser>(
        'subscription-user',
        {
          namespace: APIGATEWAY,
          typeKey: `subscriptions/${subscriptionId}/users`,
          sort_by: params.sortBy ?? 'name',
          sort_direction: params.sortDirection ?? 'asc',
          page: params.page ?? 1,
          page_size: params.pageSize ?? 100,
        },
      );

      return {
        users: [...response],
        meta: response.meta as unknown as SubscriptionUserMeta,
      } satisfies SubscriptionUserData;
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
    }
  });

  beforeModel(transition: Transition): void {
    if (!this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.USER_ACCESS)) {
      this.transition.redirectToTab(transition, 'hotel.settings.index');
    }
  }

  model(params: RouteParams): UsersModel {
    void this.loadUsersTask.perform(params);

    return {
      loadUsersTask: this.loadUsersTask,
    };
  }
}
